import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useUserData from "../hooks/useUserData";
import MUIDataTable from "mui-datatables";
import { alignProperty } from "@mui/material/styles/cssUtils";
import StatusModule from "./StatusModule";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button } from
"@mui/material";

const resourceCategories = {
  "white": ["Metals", "Baobabs", "Silicon", "Space Oats", "Nuclear Waste"],
  "#40BF00": ["Petroleum", "Vis", "Jelly Beans", "Copper", "Tin", "Silver", "Titanium", "Psion Icicles"],
  "#007FFC": ["Diamond", "Quantumum", "Alien Bacteria", "Enriched Nuclear Material", "Laconia", "Plasma Crystals", "Gold"],
  "#D86D00": ["Energon", "Dark Matter", "Frozen Blob", "Adamantium", "Rubicite", "Platinum", "Ablution Crystals", "Bacta", "Fermium", "Atmospheric Disturbances", "Dunes", "Roosts", "Trobbles", "Astral Worms"],
  "#992cff": ["Thickened Atmosphere", "Stabilized Weather", "Breathtaking Vistas", "Magnetic Miracle", "Biogeneticists Playground"],
  "#FE2712": ["Goop"]
};

const getResourceColor = (resourceName) => {
  const normalizedName = resourceName.trim().toLowerCase();
  for (const [color, resources] of Object.entries(resourceCategories)) {
    if (resources.some((res) => res.toLowerCase() === normalizedName)) {
      return color;
    }
  }
  return "white";
};

const primaryResourceNames = new Set([
"metals",
"nuclear waste",
"silicon",
"space oats",
"baobabs"]
);

const colonyTier1Names = new Set([
"astral worms",
"dunes",
"roosts",
"trobbles",
"atmospheric disturbances"]
);

const colonyTier2Names = new Set([
"thickened atmosphere",
"stabilized weather",
"breathtaking vistas",
"magnetic miracle",
"biogeneticists playground"]
);

const normalizeGalaxyName = (name) =>
  String(name ?? "")
    .toLowerCase()
    .trim()
    .replace(/['\u2019]/g, "")
    .replace(/\s+/g, " ");

const ScanDetails = () => {
  const { galaxy } = useParams();
  const navigate = useNavigate();
  const { userData, loading: loadingUser } = useUserData();

  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [techMultiplier, setTechMultiplier] = useState(1);
  const [noBps, setNoBps] = useState(false);
  const [oatsBp, setOatsBp] = useState(false);
  const [drillsBp, setDrillsBp] = useState(false);
  const [tenDrills, setTenDrills] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [owningTeam, setOwningTeam] = useState(false);
  const [owningTeamName, setOwningTeamName] = useState(false);
  const [teamNameById, setTeamNameById] = useState({});
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoTitle, setInfoTitle] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const openInfo = (title, message) => {
    setInfoTitle(title);
    setInfoMessage(message);
    setInfoOpen(true);
  };

  const closeInfo = () => {
    setInfoOpen(false);
  };

  useEffect(() => {
    if (!infoOpen) return undefined;
    const timer = setTimeout(() => {
      closeInfo();
    }, 1000);
    return () => clearTimeout(timer);
  }, [infoOpen]);

  useEffect(() => {
    fetch(`https://imp.slypandainteractive.com/galaxy_details.php?galaxy=${encodeURIComponent(galaxy)}`).
    then((r) => r.json()).
    then(setDetails).
    catch((err) => console.error("Error fetching scan details:", err));
  }, [galaxy]);


  useEffect(() => {
    fetch("/universe.json").
    then((r) => r.json()).
    then((data) => {
      const allGalaxies = Object.values(data.galaxies || {});
      const exactMatch = allGalaxies.find((g) => g.name === galaxy);
      const normalizedGalaxy = normalizeGalaxyName(galaxy);
      const match = exactMatch || allGalaxies.find(
        (g) => normalizeGalaxyName(g.name) === normalizedGalaxy
      );
      if (match) setOwningTeam(match.owningTeamID);else
      setLoading(false);
    }).
    catch((err) => {
      console.error("Error loading universe.json:", err);
      setLoading(false);
    });
  }, [galaxy]);


  useEffect(() => {
    if (owningTeam == null || owningTeam == 0)
    {
      setOwningTeamName("Nobody");
      setLoading(false);
      return;
    }

    fetch("/teams.json").
    then((r) => r.json()).
    then((data) => {
      const teamArr = Object.values(data.teams || {});
      const team = teamArr.find((t) => t.teamID === owningTeam);
      if (team) setOwningTeamName(team.name);
    }).
    catch((err) => console.error("Error loading teams.json:", err)).
    finally(() => setLoading(false));
  }, [owningTeam]);

  useEffect(() => {
    fetch("/teams.json").
    then((r) => r.json()).
    then((data) => {
      const mapping = {};
      if (data.teams) {
        Object.values(data.teams).forEach((team) => {
          if (team.teamID && team.name) {
            mapping[String(team.teamID)] = team.name;
          }
        });
      }
      setTeamNameById(mapping);
    }).
    catch((err) => console.error("Error loading teams.json:", err));
  }, []);

  const parseResources = (resourcesStr) => {
    if (!resourcesStr) return [];
    return resourcesStr.split(", ").map((res) => {
      const [name, amount] = res.split(": ");
      return { name, amount: parseInt(amount, 10) };
    });
  };

  const splitResources = (items) => {
    const primary = items.filter((res) =>
    primaryResourceNames.has(res.name.trim().toLowerCase())
    );
    const colonyTier1 = items.filter((res) =>
    colonyTier1Names.has(res.name.trim().toLowerCase())
    );
    const colonyTier2 = items.filter((res) =>
    colonyTier2Names.has(res.name.trim().toLowerCase())
    );
    const secondary = items.filter((res) => {
      const name = res.name.trim().toLowerCase();
      return (
        !primaryResourceNames.has(name) &&
        !colonyTier1Names.has(name) &&
        !colonyTier2Names.has(name));

    });
    return { primary, secondary, colonyTier1, colonyTier2 };
  };

  const copyToClipboard = async (row) => {
    const data = {
      planetName: row.planet_name,
      resources: parseResources(row.resources),
      techLevels: {
        "Steel Foundry": Math.ceil(Number(row.steel_foundry) * techMultiplier),
        "Sentient Machine Learning": Math.ceil(Number(row.sentient_machine_learning) * techMultiplier),
        "Star Bottling Plant": Math.ceil(Number(row.star_bottling_plant) * techMultiplier),
        "Figurine Workshop": Math.ceil(Number(row.figurine_workshop) * techMultiplier),
        "Giant Space Still": Math.ceil(Number(row.giant_space_still) * techMultiplier)
      },
      techLevelMultiplier: techMultiplier,
      no_bps: noBps ? 1 : 0,
      oats_bp: oatsBp ? 1 : 0,
      drills_bp: drillsBp ? 1 : 0,
      ten_drills: tenDrills ? 1 : 0
    };

    try {
      const response = await fetch("https://imp.slypandainteractive.com/generate_file.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data)
      });
      const text = await response.text();
      await navigator.clipboard.writeText(text);
      openInfo("Success!", "Trade Bay copied to clipboard.");
    } catch (err) {
      console.error("Clipboard copy failed:", err);
      openInfo("Copy failed", "Copy failed.");
    }
  };

  const copyToClipboardTeam = async (row) => {
    const data = {
      planetName: row.planet_name,
      resources: parseResources(row.resources),
      techLevels: {
        "Steel Foundry": Math.ceil(Number(row.steel_foundry) * techMultiplier),
        "Sentient Machine Learning": Math.ceil(Number(row.sentient_machine_learning) * techMultiplier),
        "Star Bottling Plant": Math.ceil(Number(row.star_bottling_plant) * techMultiplier),
        "Figurine Workshop": Math.ceil(Number(row.figurine_workshop) * techMultiplier),
        "Giant Space Still": Math.ceil(Number(row.giant_space_still) * techMultiplier)
      },
      techLevelMultiplier: techMultiplier,
      no_bps: noBps ? 1 : 0,
      oats_bp: oatsBp ? 1 : 0,
      drills_bp: drillsBp ? 1 : 0,
      ten_drills: tenDrills ? 1 : 0,


      mode: "team"
    };

    try {
      const response = await fetch("https://imp.slypandainteractive.com/generate_file.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data)
      });
      const text = await response.text();
      await navigator.clipboard.writeText(text);
      openInfo("Copied", "Team data copied to clipboard!");
    } catch (err) {
      console.error("Clipboard copy failed:", err);
      openInfo("Copy failed", "Copy failed.");
    }
  };

  const filteredDetails = details;

  const resourceTotals = useMemo(() => {
    const totals = new Map();
    filteredDetails.forEach((row) => {
      const slots = Number(row.base_slots) || 1;
      parseResources(row.resources).forEach((res) => {
        totals.set(res.name, (totals.get(res.name) || 0) + res.amount * slots);
      });
    });
    return Array.from(totals.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredDetails, techMultiplier]);

  const planetEntries = useMemo(() => {
    const byName = new Map();
    filteredDetails.forEach((row) => {
      const type = String(row?.type || "").toLowerCase();
      if (type !== "planet") return;
      const name = row.planet_name;
      if (!name || byName.has(name)) return;
      const gravity = row.gravity ?
      String(row.gravity).replace(/\s*Gravity\s*/i, "").trim() :
      "";
      byName.set(name, {
        name,
        row,
        gravity,
        temperature: row.temperature,
        atmosphere: row.atmosphere,
        suitability: row.suitability,
        baseSlots: row.base_slots,
        resources: row.resources,
        icFactories: [
        {
          label: "Steel Foundry",
          value: Math.ceil(Number(row.steel_foundry) * techMultiplier)
        },
        {
          label: "Machine Learning Institutes",
          value: Math.ceil(Number(row.sentient_machine_learning) * techMultiplier)
        },
        {
          label: "Star Bottling Plants",
          value: Math.ceil(Number(row.star_bottling_plant) * techMultiplier)
        },
        {
          label: "Paxian Workshops",
          value: Math.ceil(Number(row.figurine_workshop) * techMultiplier)
        },
        {
          label: "Giant Space Stills",
          value: Math.ceil(Number(row.giant_space_still) * techMultiplier)
        }]

      });
    });
    return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredDetails, techMultiplier]);

  const moonEntries = useMemo(() => {
    const byName = new Map();
    filteredDetails.forEach((row) => {
      const type = String(row?.type || "").toLowerCase();
      if (type !== "moon") return;
      const name = row.planet_name;
      if (!name || byName.has(name)) return;
      const gravity = row.gravity ?
      String(row.gravity).replace(/\s*Gravity\s*/i, "").trim() :
      "";
      byName.set(name, {
        name,
        row,
        gravity,
        baseSlots: row.base_slots,
        resources: row.resources,
        icFactories: [
        {
          label: "Steel Foundry",
          value: Math.ceil(Number(row.steel_foundry) * techMultiplier)
        },
        {
          label: "Machine Learning Institutes",
          value: Math.ceil(Number(row.sentient_machine_learning) * techMultiplier)
        },
        {
          label: "Star Bottling Plants",
          value: Math.ceil(Number(row.star_bottling_plant) * techMultiplier)
        },
        {
          label: "Paxian Workshops",
          value: Math.ceil(Number(row.figurine_workshop) * techMultiplier)
        },
        {
          label: "Giant Space Stills",
          value: Math.ceil(Number(row.giant_space_still) * techMultiplier)
        }]

      });
    });
    return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredDetails, techMultiplier]);

  const tableData = filteredDetails.map((row) => [
  row.upload_team && Number(row.upload_team) > 0 ?
  <img
    src={`https://www.starsonata.com/images/team_flags/game/${row.upload_team}`}
    alt={`Team ${row.upload_team}`}
    title={teamNameById[String(row.upload_team)] || `Team ${row.upload_team}`}
    style={{ maxWidth: "40px", maxHeight: "40px" }} /> :

  "",
  row.type,
  row.planet_name,
  row.gravity,
  row.temperature,
  row.atmosphere,
  row.suitability,
  row.base_slots,
  Math.ceil(Number(row.steel_foundry) * techMultiplier),
  Math.ceil(Number(row.sentient_machine_learning) * techMultiplier),
  Math.ceil(Number(row.star_bottling_plant) * techMultiplier),
  Math.ceil(Number(row.figurine_workshop) * techMultiplier),
  Math.ceil(Number(row.giant_space_still) * techMultiplier),
  <span>
      {parseResources(row.resources).map((res, i, arr) =>
    <span key={i} style={{ color: getResourceColor(res.name) }}>
          {res.name}: {res.amount}
          {i < arr.length - 1 ? ", " : ""}
        </span>
    )}
    </span>,
  row.ruins,
  <span style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
      <button className="back-button" onClick={() => copyToClipboard(row)}>
        Copy Trade Bay
      </button>
    </span>]
  );

  const makeCenteredColumn = (name) => ({
    name,
    options: {
      align: "center",
      setCellProps: () => ({
        style:
        {
          textAlign: "center",
          padding: "15px 6px"
        }
      }),
      align: "center",
      setCellHeaderProps: () => ({
        style: {
          textAlign: "center",
          whiteSpace: "normal",
          wordWrap: "break-word",
          lineHeight: "1.2",
          padding: "15px 4px"
        }
      })
    }
  });

  const columns = [

  makeCenteredColumn("Uploader"),
  makeCenteredColumn("Type"),
  makeCenteredColumn("Planet"),
  makeCenteredColumn("Gravity"),
  makeCenteredColumn("Temperature"),
  makeCenteredColumn("Atmosphere"),
  makeCenteredColumn("%"),
  makeCenteredColumn("Slots"),
  makeCenteredColumn("Girders"),
  makeCenteredColumn("Chatbots"),
  makeCenteredColumn("Fusions"),
  makeCenteredColumn("Figurines"),
  makeCenteredColumn("Whisky"),
  makeCenteredColumn("Resources"),
  makeCenteredColumn("Ruins"),
  makeCenteredColumn("Preset Trade Bay")];




  const options = {
    selectableRows: "none",
    responsive: "standard",
    rowsPerPage: 50,
    elevation: 0,
    sortOrder: {
      name: "Planet Name",
      direction: "asc"
    },
    setRowProps: () => ({ className: "custom-table-cell" }),
    setTableProps: () => ({ className: "custom-table-root" }),
    setCellProps: () => ({
      style: {
        textAlign: "center",
        padding: "4px 3px"
      }
    }),
    setCellHeaderProps: () => ({
      style: {
        textAlign: "center",
        padding: "6px",
        whiteSpace: "normal",
        wordWrap: "break-word",
        lineHeight: "1.2"
      }
    }),
    textLabels: {
      body: {
        noMatch: "No scan data found."
      }
    }
  };

  return (
    <div className="scan-details imperium-scans">
      <h1 style={{ marginBottom: '0.25rem' }}><img src={`https://www.starsonata.com/images/team_flags/game/${owningTeam}`}></img>  {galaxy} <img src={`https://www.starsonata.com/images/team_flags/game/${owningTeam}`}></img> </h1>
      <p style={{ marginTop: 0 }}><i>Owned by {owningTeamName}</i> </p> 


      <div className="scan-resource-summary">
        {resourceTotals.length === 0 ?
        "None" :
        resourceTotals.map(([name, total]) =>
        <span key={name} style={{ color: getResourceColor(name) }}>
                {name}: {total}
              </span>
        )}
      </div>

      <div className="scan-breakdown-actions scan-breakdown-actions--footer">
        <button className="back-button" onClick={() => setShowTable(true)}>
          View Sortable Table
        </button>
        <button className="back-button" onClick={() => navigate("/starsonata/team/scans")}>
          Back
        </button>
      </div>

      <div className="scan-controls">
        <div className="scan-controls-title">Trade Bay Presets</div>
        <div className="scan-controls-row">
          <label>
            <input
              type="radio"
              name="techMultiplier"
              value="0.5"
              checked={techMultiplier === 0.5}
              onChange={(e) => setTechMultiplier(parseFloat(e.target.value))} />
            
            Tech 9
          </label>
          <label>
            <input
              type="radio"
              name="techMultiplier"
              value="1"
              checked={techMultiplier === 1}
              onChange={(e) => setTechMultiplier(parseFloat(e.target.value))} />
            
            Tech 16
          </label>
          <label>
            <input
              type="radio"
              name="techMultiplier"
              value="1.5"
              checked={techMultiplier === 1.5}
              onChange={(e) => setTechMultiplier(parseFloat(e.target.value))} />
            
            Tech 20
          </label>
        </div>
        <div className="scan-controls-row">
          <label>
            <input
              type="checkbox"
              checked={noBps}
              onChange={(e) => setNoBps(e.target.checked)} />
            
            No BPs
          </label>
          <label>
            <input
              type="checkbox"
              checked={oatsBp}
              onChange={(e) => setOatsBp(e.target.checked)} />
            
            Oats BP
          </label>
          <label>
            <input
              type="checkbox"
              checked={drillsBp}
              onChange={(e) => setDrillsBp(e.target.checked)} />
            
            Drills BP
          </label>
          <label>
            <input
              type="checkbox"
              checked={tenDrills}
              onChange={(e) => setTenDrills(e.target.checked)} />
            
            +10 Drills
          </label>
        </div>
      </div>

      <div className="scan-breakdown">
        <div className="scan-breakdown-section">
          <h2>Planets</h2>
          <div className="scan-breakdown-list">
            {planetEntries.length === 0 ?
            <div className="scan-breakdown-empty">No planets found.</div> :

            planetEntries.map((planet) =>
            <div key={planet.name} className="scan-breakdown-item">
                  <div className="scan-breakdown-columns scan-breakdown-columns--compact">
                    <div className="scan-breakdown-col scan-breakdown-terra">
                      <div className="scan-breakdown-name">{planet.name}</div>
                      <div className="scan-breakdown-icon-wrap">
                        <div
                      className={`scan-breakdown-icon scan-breakdown-icon--planet scan-breakdown-icon--${
                      planet.suitability < 40 ?
                      "low" :
                      planet.suitability < 60 ?
                      "mid" :
                      "high"}`
                      }
                      aria-hidden="true" />
                    
                        <div className="scan-breakdown-slots">{planet.suitability}%</div>
                      </div>
                      <div className="scan-breakdown-slots-label">{planet.baseSlots} Slots</div>
                      {planet.row?.upload_team ?
                  <div className="scan-breakdown-uploader">
                          <img
                      className="scan-breakdown-uploader-flag"
                      src={`https://www.starsonata.com/images/team_flags/game/${planet.row.upload_team}`}
                      alt={teamNameById[String(planet.row.upload_team)] || `Team ${planet.row.upload_team}`}
                      title={teamNameById[String(planet.row.upload_team)] || `Team ${planet.row.upload_team}`} />
                    
                          <div className="scan-breakdown-uploader-label">Uploaded</div>
                        </div> :
                  null}
                    </div>
                    <div className="scan-breakdown-col scan-breakdown-resources">
                      {(() => {
                    const items = planet.resources ?
                    parseResources(planet.resources) :
                    [];
                    const factories = planet.icFactories.filter(
                      (factory) => factory.value !== 0
                    );
                    const hasResources = items.length > 0 || factories.length > 0;

                    return (
                      <>
                            <div className="scan-breakdown-sub">
                              {planet.gravity}, {planet.temperature}, {planet.atmosphere}
                            </div>
                            <div className="scan-breakdown-divider" aria-hidden="true" />
                            {items.length === 0 && !hasResources ?
                        <div className="scan-breakdown-sub">No Resources Found</div> :

                        <>
                                {(() => {
                            const { primary, secondary, colonyTier1, colonyTier2 } = splitResources(items);
                            return (
                              <>
                                      {primary.length > 0 &&
                                <div className="scan-breakdown-sub">
                                          {primary.map((res, i, arr) =>
                                  <span key={`${res.name}-${res.amount}`}>
                                              <span style={{ color: getResourceColor(res.name) }}>
                                                {res.name}: {res.amount}
                                              </span>
                                              {i < arr.length - 1 ? " " : ""}
                                            </span>
                                  )}
                                        </div>
                                }
                                      {secondary.length > 0 &&
                                <>
                                          <div className="scan-breakdown-divider" aria-hidden="true" />
                                          <div className="scan-breakdown-sub">
                                            {secondary.map((res, i, arr) =>
                                    <span key={`${res.name}-${res.amount}`}>
                                                <span style={{ color: getResourceColor(res.name) }}>
                                                  {res.name}: {res.amount}
                                                </span>
                                                {i < arr.length - 1 ? " " : ""}
                                              </span>
                                    )}
                                          </div>
                                        </>
                                }
                                      <div className="scan-breakdown-divider" aria-hidden="true" />
                                      {factories.length > 0 ?
                                <div className="scan-breakdown-sub">
                                          {factories.map((factory, i, arr) =>
                                  <span key={factory.label}>
                                              {factory.label}: {factory.value}
                                              {i < arr.length - 1 ? " " : ""}
                                            </span>
                                  )}
                                        </div> :

                                <div className="scan-breakdown-sub">No factories found</div>
                                }
                                      {(colonyTier1.length > 0 || colonyTier2.length > 0) &&
                                <div className="scan-breakdown-divider" aria-hidden="true" />
                                }
                                      {colonyTier1.length > 0 &&
                                <div className="scan-breakdown-sub">
                                          {colonyTier1.map((res, i, arr) =>
                                  <span key={`${res.name}-${res.amount}`}>
                                              <span style={{ color: getResourceColor(res.name) }}>
                                                {res.name}: {res.amount}
                                              </span>
                                              {i < arr.length - 1 ? " " : ""}
                                            </span>
                                  )}
                                        </div>
                                }
                                      {colonyTier2.length > 0 &&
                                <div className="scan-breakdown-sub">
                                          {colonyTier2.map((res, i, arr) =>
                                  <span key={`${res.name}-${res.amount}`}>
                                              <span style={{ color: getResourceColor(res.name) }}>
                                                {res.name}: {res.amount}
                                              </span>
                                              {i < arr.length - 1 ? " " : ""}
                                            </span>
                                  )}
                                        </div>
                                }
                                    </>);

                          })()}
                              </>
                        }
                            <div className="scan-breakdown-divider" aria-hidden="true" />
                            {planet.row?.ruins ?
                        <div className="scan-breakdown-sub scan-breakdown-ruins">
                                Ruins: {planet.row.ruins}
                              </div> :
                        null}
                            {planet.row?.ruins ?
                        <div className="scan-breakdown-divider" aria-hidden="true" /> :
                        null}
                          </>);

                  })()}
                      <button
                    className="back-button scan-breakdown-button"
                    onClick={() => copyToClipboard(planet.row)}>
                    
                        Copy Trade Bay
                      </button>
                    </div>
                  </div>
                </div>
            )
            }
          </div>
        </div>
        <div className="scan-breakdown-section">
          <h2>Moons</h2>
          <div className="scan-breakdown-list">
            {moonEntries.length === 0 ?
            <div className="scan-breakdown-empty">No moons found.</div> :

            moonEntries.map((moon) =>
            <div key={moon.name} className="scan-breakdown-item">
                  <div className="scan-breakdown-columns scan-breakdown-columns--compact">
                    <div className="scan-breakdown-col scan-breakdown-terra">
                      <div className="scan-breakdown-name">{moon.name}</div>
                      <div className="scan-breakdown-icon scan-breakdown-icon--moon" aria-hidden="true" />
                      {moon.row?.upload_team ?
                  <div className="scan-breakdown-uploader">
                          <img
                      className="scan-breakdown-uploader-flag"
                      src={`https://www.starsonata.com/images/team_flags/game/${moon.row.upload_team}`}
                      alt={teamNameById[String(moon.row.upload_team)] || `Team ${moon.row.upload_team}`}
                      title={teamNameById[String(moon.row.upload_team)] || `Team ${moon.row.upload_team}`} />
                    
                          <div className="scan-breakdown-uploader-label">Uploaded</div>
                        </div> :
                  null}
                    </div>
                    <div className="scan-breakdown-col scan-breakdown-resources">
                      {(() => {
                    const items = moon.resources ? parseResources(moon.resources) : [];
                    const factories = moon.icFactories.filter(
                      (factory) => factory.value !== 0
                    );
                    const hasResources = items.length > 0 || factories.length > 0;

                    return (
                      <>
                            <div className="scan-breakdown-sub">Orbiting: [Unknown]</div>
                            <div className="scan-breakdown-divider" aria-hidden="true" />
                            {items.length === 0 && !hasResources ?
                        <div className="scan-breakdown-sub">No Resources Found</div> :

                        <>
                                {(() => {
                            const { primary, secondary, colonyTier1, colonyTier2 } = splitResources(items);
                            return (
                              <>
                                      {primary.length > 0 &&
                                <div className="scan-breakdown-sub">
                                          {primary.map((res, i, arr) =>
                                  <span key={`${res.name}-${res.amount}`}>
                                              <span style={{ color: getResourceColor(res.name) }}>
                                                {res.name}: {res.amount}
                                              </span>
                                              {i < arr.length - 1 ? " " : ""}
                                            </span>
                                  )}
                                        </div>
                                }
                                      {secondary.length > 0 &&
                                <>
                                          <div className="scan-breakdown-divider" aria-hidden="true" />
                                          <div className="scan-breakdown-sub">
                                            {secondary.map((res, i, arr) =>
                                    <span key={`${res.name}-${res.amount}`}>
                                                <span style={{ color: getResourceColor(res.name) }}>
                                                  {res.name}: {res.amount}
                                                </span>
                                                {i < arr.length - 1 ? " " : ""}
                                              </span>
                                    )}
                                          </div>
                                        </>
                                }
                                      <div className="scan-breakdown-divider" aria-hidden="true" />
                                      {factories.length > 0 ?
                                <div className="scan-breakdown-sub">
                                          {factories.map((factory, i, arr) =>
                                  <span key={factory.label}>
                                              {factory.label}: {factory.value}
                                              {i < arr.length - 1 ? " " : ""}
                                            </span>
                                  )}
                                        </div> :

                                <div className="scan-breakdown-sub">No factories found</div>
                                }
                                      {(colonyTier1.length > 0 || colonyTier2.length > 0) &&
                                <div className="scan-breakdown-divider" aria-hidden="true" />
                                }
                                      {colonyTier1.length > 0 &&
                                <div className="scan-breakdown-sub">
                                          {colonyTier1.map((res, i, arr) =>
                                  <span key={`${res.name}-${res.amount}`}>
                                              <span style={{ color: getResourceColor(res.name) }}>
                                                {res.name}: {res.amount}
                                              </span>
                                              {i < arr.length - 1 ? " " : ""}
                                            </span>
                                  )}
                                        </div>
                                }
                                      {colonyTier2.length > 0 &&
                                <div className="scan-breakdown-sub">
                                          {colonyTier2.map((res, i, arr) =>
                                  <span key={`${res.name}-${res.amount}`}>
                                              <span style={{ color: getResourceColor(res.name) }}>
                                                {res.name}: {res.amount}
                                              </span>
                                              {i < arr.length - 1 ? " " : ""}
                                            </span>
                                  )}
                                        </div>
                                }
                                    </>);

                          })()}
                              </>
                        }
                            <div className="scan-breakdown-divider" aria-hidden="true" />
                            {moon.row?.ruins ?
                        <div className="scan-breakdown-sub scan-breakdown-ruins">
                                Ruins: {moon.row.ruins}
                              </div> :
                        null}
                            {moon.row?.ruins ?
                        <div className="scan-breakdown-divider" aria-hidden="true" /> :
                        null}
                          </>);

                  })()}
                      <button
                    className="back-button scan-breakdown-button"
                    onClick={() => copyToClipboard(moon.row)}>
                    
                        Copy Trade Bay
                      </button>
                    </div>
                  </div>
                </div>
            )
            }
          </div>
        </div>
      </div>

      {showTable && !loading && !loadingUser &&
      <div className="scan-table-modal" role="dialog" aria-modal="true">
          <div className="scan-table-modal__backdrop" onClick={() => setShowTable(false)} />
          <div className="scan-table-modal__panel">
            <div className="scan-table-modal__header">
              <h3>Sortable Table View</h3>
              <button className="back-button" onClick={() => setShowTable(false)}>
                Close
              </button>
            </div>
            <MUIDataTable title={null} data={tableData} columns={columns} options={options} />
          </div>
        </div>
      }

      <Dialog
        open={infoOpen}
        onClose={closeInfo}
        PaperProps={{ className: "leader-confirm-paper" }}>
        
        <DialogTitle sx={{ textAlign: "center" }}>{infoTitle}</DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          <DialogContentText className="leader-confirm-subtext">
            {infoMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button
            onClick={closeInfo}
            sx={{
              backgroundColor: "#4a4a4a",
              color: "#fff",
              "&:hover": { backgroundColor: "#5a5a5a" }
            }}>
            
            Close
          </Button>
        </DialogActions>
      </Dialog>

      </div>);


};

export default ScanDetails;
